import { useEffect, useState } from "react";
import { Send, CheckCircle2, Rocket, Apple } from "lucide-react";
import { analytics } from "@heycatch/sdk";
import { Reveal } from "./Reveal";

const AndroidIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <g fill="currentColor">
      <path d="M6.5 9a5.5 5.5 0 0 1 11 0z" />
      <rect x="6.5" y="10" width="11" height="7.5" rx="2" />
      <rect x="3.4" y="10.5" width="2.3" height="6" rx="1.15" />
      <rect x="18.3" y="10.5" width="2.3" height="6" rx="1.15" />
      <rect x="8.2" y="18" width="2.3" height="3.8" rx="1.15" />
      <rect x="13.5" y="18" width="2.3" height="3.8" rx="1.15" />
    </g>
    <path d="M8.6 3.2 10 5.3M15.4 3.2 14 5.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    <circle cx="9.9" cy="6.6" r="0.9" fill="#1F1C1A" />
    <circle cx="14.1" cy="6.6" r="0.9" fill="#1F1C1A" />
  </svg>
);

const Tab = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
      active
        ? "bg-crave-gold text-crave-bg"
        : "border border-crave-border text-crave-muted hover:border-crave-gold/50 hover:text-crave-cream"
    }`}
  >
    {children}
  </button>
);

const inputClass =
  "flex-1 rounded-full border border-crave-border bg-crave-card px-6 py-3.5 text-sm text-crave-cream placeholder:text-crave-muted focus:border-crave-gold focus:outline-none w-full";

export const GetStarted = () => {
  const [mode, setMode] = useState("waitlist");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const [tester, setTester] = useState({ name: "", email: "", device: "" });
  const [testerStatus, setTesterStatus] = useState("idle");
  const [testerError, setTesterError] = useState("");

  useEffect(() => {
    const openTesters = () => setMode("tester");
    window.addEventListener("fc:open-testers", openTesters);
    return () => window.removeEventListener("fc:open-testers", openTesters);
  }, []);

  const post = async (path, body) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.detail || "Something went wrong. Please try again.");
    }
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await post("waitlist", { email });
      analytics.trackEvent("waitlist_signup", { properties: { platform: "ios" } });
      setStatus("success");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  const submitTester = async (e) => {
    e.preventDefault();
    setTesterStatus("loading");
    setTesterError("");
    try {
      await post("testers", {
        name: tester.name,
        email: tester.email,
        device: tester.device || undefined,
        platform: "android",
      });
      setTesterStatus("success");
      analytics.trackEvent("tester_signup", { properties: { platform: "android" } });
      setTester({ name: "", email: "", device: "" });
    } catch (err) {
      setTesterStatus("error");
      setTesterError(err.message);
    }
  };

  return (
    <section
      id="get-started"
      data-testid="get-started-section"
      className="relative scroll-mt-24 overflow-hidden py-24 md:py-32"
    >
      <img
        src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=1600&auto=format&fit=crop"
        alt="A warm restaurant table with a plated dinner, ready to enjoy"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-crave-bg via-crave-bg/80 to-crave-bg"
      />

      <div className="relative mx-auto max-w-3xl px-6 text-center md:px-12">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Early Access</p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl">
            Join the Foodie Crave journey
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-crave-cream/75">
            Foodie Crave is growing, and we'd love for you to be part of the journey. Help test the
            Android app or join the iOS waiting list to stay informed about future availability. Be
            among the first to discover, save, and experience more places to eat.
          </p>
          <p
            data-testid="early-access-message"
            className="mx-auto mt-8 max-w-md rounded-2xl border border-crave-gold/30 bg-crave-gold/10 px-6 py-4 text-sm font-semibold leading-relaxed text-crave-gold"
          >
            Help shape Foodie Crave before launch. Join our early testers and be part of improving
            the dining experience.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Tab active={mode === "waitlist"} onClick={() => setMode("waitlist")} testId="waitlist-tab-button">
              <Apple className="h-4 w-4" aria-hidden="true" />
              iOS Waiting List
            </Tab>
            <Tab active={mode === "tester"} onClick={() => setMode("tester")} testId="tester-tab-button">
              <AndroidIcon className="h-4 w-4" />
              Android Testing
            </Tab>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {mode === "waitlist" ? (
            status === "success" ? (
              <div
                data-testid="waitlist-success-message"
                className="mx-auto mt-8 flex max-w-md items-center justify-center gap-3 rounded-full border border-crave-gold/40 bg-crave-card px-6 py-4"
              >
                <CheckCircle2 className="h-5 w-5 text-crave-gold" aria-hidden="true" />
                <p className="text-sm font-semibold text-crave-cream">
                  You're on the list — we'll be in touch soon.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submitWaitlist}
                data-testid="waitlist-form"
                className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row"
              >
                <label htmlFor="waitlist-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="waitlist-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="waitlist-email-input"
                  className={inputClass}
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="waitlist-submit-button"
                  className="flex items-center justify-center gap-2 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors duration-300 hover:bg-crave-cream disabled:opacity-60"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  {status === "loading" ? "Joining…" : "Join the iOS Waitlist"}
                </button>
              </form>
            )
          ) : testerStatus === "success" ? (
            <div
              data-testid="tester-success-message"
              className="mx-auto mt-8 flex max-w-md items-center justify-center gap-3 rounded-full border border-crave-gold/40 bg-crave-card px-6 py-4"
            >
              <CheckCircle2 className="h-5 w-5 text-crave-gold" aria-hidden="true" />
              <p className="text-sm font-semibold text-crave-cream">
                You're on the tester list — check your inbox for confirmation.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submitTester}
              data-testid="tester-form"
              className="mx-auto mt-8 flex max-w-md flex-col gap-3"
            >
              <p
                data-testid="tester-android-note"
                className="rounded-full border border-crave-gold/30 bg-crave-gold/10 px-4 py-2 text-xs font-semibold text-crave-gold"
              >
                In testing now — accepting Android testers on Google Play
              </p>
              <label htmlFor="tester-name" className="sr-only">
                Your name
              </label>
              <input
                id="tester-name"
                type="text"
                required
                value={tester.name}
                onChange={(e) => setTester({ ...tester, name: e.target.value })}
                placeholder="Your name"
                data-testid="tester-name-input"
                className={inputClass}
              />
              <label htmlFor="tester-email" className="sr-only">
                Email address
              </label>
              <input
                id="tester-email"
                type="email"
                required
                value={tester.email}
                onChange={(e) => setTester({ ...tester, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="tester-email-input"
                className={inputClass}
              />
              <label htmlFor="tester-device" className="sr-only">
                Device (optional)
              </label>
              <input
                id="tester-device"
                type="text"
                value={tester.device}
                onChange={(e) => setTester({ ...tester, device: e.target.value })}
                placeholder="Android device (optional, e.g. Pixel 8)"
                data-testid="tester-device-input"
                className={inputClass}
              />
              <button
                type="submit"
                disabled={testerStatus === "loading"}
                data-testid="tester-submit-button"
                className="mt-1 flex items-center justify-center gap-2 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors duration-300 hover:bg-crave-cream disabled:opacity-60"
              >
                <Rocket className="h-4 w-4" aria-hidden="true" />
                {testerStatus === "loading" ? "Joining…" : "Become an Android Tester"}
              </button>
            </form>
          )}
          {mode === "waitlist" && status === "error" && (
            <p data-testid="waitlist-error-message" className="mt-4 text-sm text-crave-gold">
              {error}
            </p>
          )}
          {mode === "tester" && testerStatus === "error" && (
            <p data-testid="tester-error-message" className="mt-4 text-sm text-crave-gold">
              {testerError}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};
